import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader, Brain, Languages, CheckCircle, Clock, ScanLine, FileText } from 'lucide-react';
import { LANGUAGES } from '../constants/languages';
import { formatDuration } from '../utils/formatDuration';
import './TranslationProgress.css';

function TranslationProgress({
  currentStage,
  iteration,
  maxIterations,
  message,
  pageDone,
  pageTotal,
  startTime,
  detectedLanguage,
}) {
  const { t } = useTranslation('components');
  const [elapsed, setElapsed] = useState(0);
  const [hadOcrPhase, setHadOcrPhase] = useState(false);
  const [hadConvertingPhase, setHadConvertingPhase] = useState(false);
  const [tipIndex, setTipIndex] = useState(0);

  // Rotating one-liners so the wait feels alive instead of a static
  // "in progress". Cycles every 2.8s; harmless if the locale has only one.
  const rawTips = t('progress.working', { returnObjects: true });
  const tips = Array.isArray(rawTips) ? rawTips : [];
  useEffect(() => {
    if (tips.length <= 1) return undefined;
    const id = setInterval(() => setTipIndex((i) => (i + 1) % tips.length), 2800);
    return () => clearInterval(id);
  }, [tips.length]);

  useEffect(() => {
    if (!startTime) return;

    setElapsed(Math.floor((Date.now() - startTime) / 1000));

    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime]);

  const isOcrStage = typeof currentStage === 'string' && currentStage.startsWith('ocr');

  const isConvertingStage = currentStage === 'converting';

  useEffect(() => {
    if (isOcrStage) setHadOcrPhase(true);
  }, [isOcrStage]);

  useEffect(() => {
    if (isConvertingStage) setHadConvertingPhase(true);
  }, [isConvertingStage]);

  // Once language is detected we have a real flag to show on the Preparing
  // chip. While it isn't yet known (initial state, scan still processing),
  // fall back to the generic Loader icon.
  const langInfo =
    detectedLanguage && detectedLanguage !== 'auto'
      ? LANGUAGES[detectedLanguage]
      : null;
  const preparingFlag = langInfo?.flag || null;

  const allStages = [
    { id: 'ocr', label: t('progress.stages.ocr'), icon: ScanLine },
    { id: 'converting', label: t('progress.stages.converting'), icon: FileText },
    { id: 'preparing', label: t('progress.stages.preparing'), icon: Loader, flag: preparingFlag },
    { id: 'planning', label: t('progress.stages.planning'), icon: Brain },
    { id: 'translating', label: t('progress.stages.translating'), icon: Languages },
    { id: 'critiquing', label: t('progress.stages.critiquing'), icon: CheckCircle },
  ];

  // A scan's reading and a PDF's setting as Word show only on the documents that go through them.
  const stages = allStages.filter(
    (s) => (s.id !== 'ocr' || hadOcrPhase || isOcrStage)
      && (s.id !== 'converting' || hadConvertingPhase || isConvertingStage),
  );
  const normalizedStage = isOcrStage ? 'ocr' : currentStage;
  const currentIndex = stages.findIndex((s) => s.id === normalizedStage);

  return (
    <div className="translation-progress">
      <div className="progress-stages">
        {stages.map((stage, index) => {
          const Icon = stage.icon;
          const isActive = stage.id === normalizedStage;
          const isCompleted = index < currentIndex;

          return (
            <div
              key={stage.id}
              className={`progress-stage ${isActive ? 'active' : ''} ${
                isCompleted ? 'completed' : ''
              }`}
            >
              <div className="stage-icon">
                {isActive ? (
                  stage.flag ? (
                    <span className="stage-flag" role="img" aria-label={stage.label}>
                      {stage.flag}
                    </span>
                  ) : (
                    <div className="spinner" />
                  )
                ) : stage.flag && (isCompleted || stage.id === 'preparing') ? (
                  <span className="stage-flag" role="img" aria-label={stage.label}>
                    {stage.flag}
                  </span>
                ) : (
                  <Icon size={20} />
                )}
              </div>
              <span className="stage-label">{stage.label}</span>
            </div>
          );
        })}
      </div>

      {(isOcrStage || isConvertingStage) && pageTotal ? (
        <div className="progress-detail">
          {t('progress.page', { done: pageDone || 0, total: pageTotal })}
        </div>
      ) : tips.length ? (
        <div className="progress-detail progress-tip" key={tipIndex}>
          {tips[tipIndex]}
        </div>
      ) : null}

      {startTime && (
        <div className="progress-elapsed-bar">
          <Clock size={13} />
          {formatDuration(elapsed)}
        </div>
      )}

      <div className="progress-bar-container">
        <div
          className="progress-bar"
          style={{
            width: `${((currentIndex + 1) / stages.length) * 100}%`,
          }}
        />
      </div>
    </div>
  );
}

export default TranslationProgress;
